import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Section } from '@vnetwork/ui/layout';
import { Button, Textarea, Card, CardBody } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useWaapPrefetch } from '@/api/waap';
import { errMessage } from '@/pages/services/waap/waap-util';
import styles from './PrefetchTab.module.css';

const MAX_URLS = 25;

/**
 * Prefetch — website-detail tab. Warm the cache with up to 25 full URLs
 * (one per line) → POST /waf/eagle/prefetch. Website fixed by the detail route.
 */
export function PrefetchTab() {
  const { domainId } = useParams<{ domainId: string }>();
  const { show } = useToast();
  const [text, setText] = useState('');

  const prefetch = useWaapPrefetch(domainId ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'Prefetch requested' }),
    onError: (e) => show({ tone: 'danger', title: 'Prefetch failed', description: errMessage(e) }),
  });

  const urls = text.split(/[\s]+/).map((s) => s.trim()).filter(Boolean);
  const tooMany = urls.length > MAX_URLS;
  const valid = urls.length > 0 && !tooMany;

  return (
    <Section
      title="Prefetch"
      description="Warm the cache with the latest version from the origin. Enter 1-25 full URLs separated by space or newline."
    >
      <Card>
        <CardBody className={styles.bodyGrid}>
          <Textarea
            rows={8}
            placeholder={'https://mywebsite.com/foo/bar.txt'}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div
            className={styles.count}
            style={{ color: tooMany ? 'var(--vn-danger-text)' : 'var(--vn-fg-muted)' }}
          >
            {urls.length}/{MAX_URLS} URLs{tooMany ? ' — too many, max 25.' : ''}
          </div>
          <div className={styles.hint}>
            Default limits: 10 URLs/minute, 100 URLs/hour, 1000 URLs/day. Contact the provider for domain-level limits.
          </div>
          <div>
            <Button disabled={!valid || prefetch.isPending} onClick={() => prefetch.mutate({ urls })}>
              {prefetch.isPending ? 'Prefetching…' : 'Prefetch'}
            </Button>
          </div>
        </CardBody>
      </Card>
    </Section>
  );
}
